<template>
    <div :class="[
        `${prefixCls}`,
        `${prefixCls}-theme-${props.theme}`,
        {
            [`${prefixCls}--${props.size}`]: !!props.size,
            [`${prefixCls}-disabled`]: props.disabled,
            [`${prefixCls}-focused`]: state.focused,
            [`${prefixCls}-error`]: state.isError,
            [`${prefixCls}-no-blank`]: props.modelValue !== '' && !state.focused,
        },
    ]">
        <div :class="`${prefixCls}-handler-wrapper`" v-if="!suffix">
            <a :class="`${prefixCls}-handler`" v-repeat-click="increase" @keydown.enter="increase" @mouse.down.stop="preventDefault">
                <!-- <i :class="[
                    `${prefixCls}-handler-inner ${prefixCls}-handler-inner-up`,
                    {
                        'disabled': state.upDisabled,
                    }
                ]"> -->
                    <pt-icon icon="pt-icon-asc" :style="{ width: '100%', height: '100%' }" :class="[
                    `${prefixCls}-handler-inner ${prefixCls}-handler-inner-up`,
                    {
                        'disabled': state.upDisabled,
                    }
                ]"></pt-icon>
                <!-- </i> -->
            </a>
            <a
                :class="`${prefixCls}-handler`"
                v-repeat-click="decrease"
                @keydown.enter="decrease"
                @mouse.down.stop="preventDefault"
            >
                <!-- <i :class="[
                `${prefixCls}-handler-inner ${prefixCls}-handler-inner-down`,
                    {
                        'disabled': state.downDisabled,
                    },
                ]"> -->
                    <pt-icon icon="pt-icon-desc" :style="{ width: '100%', height: '100%' }" :class="[
                `${prefixCls}-handler-inner ${prefixCls}-handler-inner-down`,
                    {
                        'disabled': state.downDisabled,
                    },
                ]"></pt-icon>
                <!-- </i> -->
            </a>
        </div>
        <div :class="`${prefixCls}-handler-wrapper suffix`" v-else>{{ suffixContent }}</div>
        <div :class="`${prefixCls}-input-wrapper`">
            <input
                ref="inputRef"
                type="text"
                autocomplete="off"
                class="pt-input__number_inner"
                :class="`${prefixCls}-input`"
                :disabled="disabled"
                :placeholder="placeholder"
                @keydown.up.prevent="increase"
                @keydown.down.prevent="decrease"
                @keydown.enter="handleEnter"
                @focus="focus"
                @blur="blur"
                @keydown.38.stop="up"
                @keydown.40.stop="down"
                @input="handleInput"
            />
            <div class="pt-input__number-unit" v-if="unit">
                {{ unit }}
            </div>
        </div>
    </div>
</template>
<script lang="ts">
if (!String.prototype.padStart) {
    String.prototype.padStart = function padStart(targetLength, padString) {
        targetLength = targetLength >> 0; //floor if number or convert non-number to 0;
        padString = String(padString || ' ');
        if (this.length > targetLength) {
            return String(this);
        } else {
            targetLength = targetLength - this.length;
            if (targetLength > padString.length) {
                padString += padString.repeat(targetLength / padString.length); //append to original to ensure we are longer than needed
            }
            return padString.slice(0, targetLength) + String(this);
        }
    };
}
export default {
    name: 'PtInputNumber',
};
</script>

<script setup lang="ts">
import { computed, defineComponent, reactive, ref, watch, inject, onMounted, onUpdated, nextTick } from 'vue';
import { isInteger, addNum } from '../../../utils/util';
import { UPDATE_MODEL_EVENT } from '../../../utils/constants';
import vRepeatClick from '../../directives/repeat-click';
interface Props {
    modelValue: string | number;
    isPadStart?: boolean;
    max?: number;
    min?: number;
    step?: number;
    // value: number | string;
    size?: 'default' | 'big' | 'large' | 'xlarge';
    disabled?: boolean;
    placeholder?: string | number;
    //是否允许为空
    isEmpty?: boolean;
    //是否允许小数
    isDecimal?: boolean;
    //是否允许负数
    isNegative?: boolean;
    //当value为空时，是否需要校验极限值(特殊情况对应Y轴多个组件之间的比较)
    isCheckEmptyValue?: boolean;
    theme?: string;
    //添加后缀
    suffix?: boolean;
    suffixContent?: string;
    precision?: number;
    unit?: string;
}
const props = withDefaults(defineProps<Props>(), {
    isPadStart: false,
    max: Infinity,
    min: -Infinity,
    step: 1,
    value: 1,
    size: 'default',
    disabled: false,
    isEmpty: true,
    isDecimal: false,
    isNegative: true,
    isCheckEmptyValue: true,
    theme: 'light',
    suffix: false,
});
const emit = defineEmits([UPDATE_MODEL_EVENT, 'enter', 'input', 'change']);
const prefixCls = 'pt-input__number';
const inputRef = ref(null!);
const state = reactive({
    focused: false,
    upDisabled: false,
    downDisabled: false,
    prevVal: null, //上一次有效值
    lastInputVal: null, //上一次有效输入值
    isError: false,
});
const minDisabled = computed(() => {
    return _decrease(props.modelValue, props.step) < props.min;
});
const maxDisabled = computed(() => {
    return _increase(props.modelValue, props.step) > props.max;
});
const numPrecision = computed(() => {
    const { value, step, precision } = props;
    const stepPrecision = getPrecision(step);
    if (precision !== undefined) {
        if (stepPrecision > precision) {
            console.warn('[Element Warn][InputNumber]precision should not be less than the decimal places of step');
        }
        return precision;
    } else {
        return Math.max(getPrecision(value), stepPrecision);
    }
});
const inputValue = computed(() => {
    if (props.isPadStart) {
        return props.modelValue ? props.modelValue?.toString()?.padStart(2, '0') : '00';
    } else {
        return props.modelValue;
    }
});
const setValue = (val) => {
    state.prevVal = val;
    emit(UPDATE_MODEL_EVENT, +val)
    emit('input', +val);
    emit('change', +val);
    setInputValue();
};
const init = () => {
    state.prevVal = props.modelValue;
    state.lastInputVal = props.modelValue;
    setInputValue();
};
const changeStep = (type: string, e: { target: any }) => {
    if (props.disabled) {
        return false;
    }
    const targetVal = Number(e.target.value);
    let val = Number(props.modelValue);
    const step = Number(props.step);
    if (isNaN(val)) {
        return false;
    }

    // input a number, and key up or down
    if (!isNaN(targetVal)) {
        if (type === 'up') {
            if (addNum(targetVal, step) <= props.max) {
                val = targetVal;
            } else {
                return false;
            }
        } else if (type === 'down') {
            if (addNum(targetVal, -step) >= props.min) {
                val = targetVal;
            } else {
                return false;
            }
        }
    }

    if (type === 'up') {
        let o = isInteger(val) ? val : parseInt(val);
        val = Math.max(addNum(o, step), props.min);
    } else if (type === 'down') {
        let o = isInteger(val) ? val : parseInt(val) + 1;
        val = Math.min(addNum(o, -step), props.max);
    }

    if (!valIsError(val)) {
        setValue(val);
    }
};
const preventDefault = (e) => {
    e.preventDefault();
};

const up = (e: { target: { value: any } }) => {
    const targetVal = Number(e.target.value);
    if (state.upDisabled && isNaN(targetVal)) {
        return false;
    }
    changeStep('up', e);
};
const down = (e) => {
    const targetVal = Number(e.target.value);
    if (state.downDisabled && isNaN(targetVal)) {
        return false;
    }
    changeStep('down', e);
};
const handleEnter = () => {
    emit('enter');
};

const toPrecision = (num: number, precision: number | undefined = numPrecision.value) => {
    if (precision === undefined) precision = numPrecision.value;
    return parseFloat(parseFloat(Number(num).toFixed(precision)));
};
const getPrecision = (value) => {
    if (value === undefined) return 0;
    const valueString = value.toString();
    const dotPosition = valueString.indexOf('.');
    let precision = 0;
    if (dotPosition !== -1) {
        precision = valueString.length - dotPosition - 1;
    }
    return precision;
};

const increase = () => {
    if (props.disabled || maxDisabled.value) return;
    const value = props.modelValue || 0;
    const newVal = _increase(value, props.step);
    setValue(newVal);
};
const decrease = () => {
    if (props.disabled || minDisabled.value) return;
    const value = props.modelValue || 0;
    const newVal = _decrease(value, props.step);
    setValue(newVal);
};
const focus = () => {
    state.focused = true;
};
const blur = (e) => {
    state.focused = false;
    resetTargetVal(e);
    nextTick(() => {
        e.target.value = inputValue.value;
    });
};
const keyDown = (e) => {
    if (e.keyCode === 38) {
        e.preventDefault();
        up(e);
    } else if (e.keyCode === 40) {
        e.preventDefault();
        down(e);
    }
};
const handleInput = (e) => {
    let val = e.target.value.trim();
    let formated = parseFloat(val);

    if (isInputPass(val)) {
        state.lastInputVal = val;
    } else {
        state.lastInputVal = isValueNumber(formated) ? formated : state.lastInputVal;
    }
    state.isError = valIsError(state.lastInputVal);
    if (!state.isError) setValue(state.lastInputVal);
};

const valIsError = (val) => {
    if (val === '') {
        return !props.isEmpty || props.isCheckEmptyValue;
    } else {
        return !isValueNumber(val) || val > props.max || val < props.min;
    }
};
const changeVal = (val) => {
    if (isValueNumber(val) || val === 0) {
        val = Number(val);
        const step = props.step;

        state.upDisabled = val + step > props.max;
        state.downDisabled = val - step < props.min;
    } else {
        if (val === '' && props.isEmpty) {
            state.upDisabled = false;
            state.downDisabled = false;
        } else {
            state.upDisabled = true;
            state.downDisabled = true;
        }
    }
};
const resetTargetVal = () => {
    if (state.isError) {
        setValue(state.prevVal);
        state.isError = false;
    } else {
        setValue(props.modelValue === '' ? '' : parseFloat(props.modelValue));
    }
};
const isValueNumber = (val) => {
    let reg = /^\d+$/;

    if (props.isDecimal && props.isNegative) {
        reg = /(^-?\d+?(\.)?(\d+)$)|(^-?\d+$)/;
    } else if (props.isDecimal) {
        reg = /(^\d+(\.)?(\d+)$)|(^\d+$)/;
    } else if (props.isNegative) {
        reg = /^-?\d+$/;
    }
    return reg.test(val + '');
};
const isInputPass = (val) => {
    let reg = /^\d*$/;

    if (props.isDecimal && props.isNegative) {
        reg = /^-?(\d+)?(\.)?(\d+)?$/;
    } else if (props.isDecimal) {
        reg = /^(\d*)?(\.)?(\d*)?$/;
    } else if (props.isNegative) {
        reg = /^-?\d*$/;
    }
    return reg.test(val + '');
};

watch(
    () => [inputValue.value, props.max, props.min],
    () => {
        init();
        changeVal(props.modelValue);
    },
);
const setInputValue = () => {
      if (!inputRef.value || inputRef.value.value === inputValue.value) return
      inputRef.value.value = inputValue.value
    }
onMounted(() => {
    init();
    setInputValue();
    changeVal(props.modelValue);
});
function _increase(val: any, step: number) {
    if (typeof val !== 'number' && val !== undefined) return props.modelValue;

    const precisionFactor = Math.pow(10, numPrecision.value);
    // Solve the accuracy problem of JS decimal calculation by converting the value to integer.
    return toPrecision((precisionFactor * val + precisionFactor * step) / precisionFactor);
}
function _decrease(val: any, step: number) {
    if (typeof val !== 'number' && val !== undefined) return props.modelValue;

    const precisionFactor = Math.pow(10, numPrecision.value);

    return toPrecision((precisionFactor * val - precisionFactor * step) / precisionFactor);
}
</script>

<style lang="scss" src="../style/input-number.scss"></style>
